import React, { useCallback, useMemo, useState } from "react";
import { Col, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import {
    BsArrowLeftSquare,
    BsPlusSquare,
    BsPencilSquare,
    BsTrash,
} from "react-icons/bs";
import { IItbi } from "../../../../../shared/providers/itbi/interfaces";
import { DotLine } from "../../../bci/styles";
import { dateMask } from "../../../../../shared/utils/mask.utils";
import { Popup } from "../../../../global/popup";
import { ApiErrorHandler } from "../../../../../shared/utils/errors.utils";
import { showToast } from "../../../../global/toast";
import { ItbiEdit } from "./edit";
import { ItbiApiService } from "../../../../../shared/services/api/itbi-api.service";
import { FeaturesEnum } from "../../../../../shared/enums/features.enum";
import { useAuth } from "../../../../../shared/hooks/useAuth";

interface Props {
    info: Record<string, string>;
    data?: IItbi[];
    lastYear: string;
    doAfterSubmit(register: string): void;
}

export const ItbiEditableYear: React.FC<Props> = ({
    info,
    data,
    lastYear,
    doAfterSubmit,
}) => {
    const { user } = useAuth();
    const [enableCreate, setEnableCreate] = useState(false);
    const [enableEdit, setEnableEdit] = useState(false);
    const [beingEdited, setBeingEdited] = useState<number | undefined>(
        undefined
    );
    const [popup, setPopup] = useState<{
        show: boolean;
        id: number | undefined;
    }>({ show: false, id: undefined });

    const filteredData = useMemo(
        () => data?.filter((item) => item.exercicio === lastYear) || [],
        [data, lastYear]
    );

    const handleEnableEdit = (id: number | undefined) => {
        if (id !== undefined) {
            setBeingEdited(id);
            setEnableEdit(true);
        }
    };

    const handleEnableCreate = () => {
        setEnableCreate((prev) => !prev);
    };

    const handleDelete = (id: number | undefined) => {
        setPopup({ show: true, id });
    };

    const handleDeleteCancel = () => {
        setPopup({ show: false, id: undefined });
    };

    const handleDeleteConfirm = useCallback(async () => {
        if (popup.show && popup.id) {
            try {
                await ItbiApiService.deleteItbi(popup.id);
                showToast({
                    message: `ITBI removido com sucesso`,
                    type: "success",
                });
                doAfterSubmit(info.cadastrogeral);
            } catch (err) {
                ApiErrorHandler(err, "warn");
            }
            setPopup({ show: false, id: undefined });
        }
    }, [popup, doAfterSubmit, info.cadastrogeral]);

    const handleAfterEditSubmit = (register: string) => {
        setEnableEdit(false);
        doAfterSubmit(register);
    };

    return (
        <>
            {!enableEdit && (
                <div className="grid grid-cols-3 grid-rows-1">
                    <OverlayTrigger
                        placement="bottom"
                        overlay={
                            <Tooltip>
                                {enableCreate ? "VOLTAR" : "ADICIONAR"}
                            </Tooltip>
                        }
                    >
                        <div className="mb-3 flex space-x-2 justify-center">
                            {user.authorizationsByFeatureName[FeaturesEnum.ITBI]
                                ?.canCreate && (
                                <button
                                    type="button"
                                    onClick={handleEnableCreate}
                                >
                                    {enableCreate ? (
                                        <BsArrowLeftSquare
                                            color="teal"
                                            size={20}
                                        />
                                    ) : (
                                        <BsPlusSquare color="teal" size={20} />
                                    )}
                                </button>
                            )}
                        </div>
                    </OverlayTrigger>
                </div>
            )}

            {enableCreate && (
                <ItbiEdit
                    register={info.cadastrogeral}
                    doAfterSubmit={handleAfterEditSubmit}
                />
            )}

            {enableEdit && beingEdited !== null && (
                <ItbiEdit
                    register={info.cadastrogeral}
                    data={data?.find((item) => item.id === beingEdited)}
                    backButton={() => setEnableEdit(false)}
                    doAfterSubmit={handleAfterEditSubmit}
                />
            )}

            {popup.show && (
                <Popup
                    phrase="Tem certeza que deseja remover o ITBI?"
                    show
                    handleConfirm={handleDeleteConfirm}
                    handleCancel={handleDeleteCancel}
                />
            )}

            {!enableCreate &&
                !enableEdit &&
                user.authorizationsByFeatureName[FeaturesEnum.ITBI]
                    ?.canRead && (
                    <div>
                        {filteredData.length > 0 ? (
                            filteredData.map((value, index) => (
                                <div key={value.id}>
                                    {index > 0 && <DotLine />}
                                    <Row>
                                        <Col>
                                            <Form.Label>
                                                Data Lançamento
                                            </Form.Label>
                                            <Form.Control
                                                disabled
                                                value={dateMask(
                                                    value.datalancamento,
                                                    "date"
                                                )}
                                            />
                                        </Col>
                                        <Col>
                                            <Form.Label>Valor</Form.Label>
                                            <Form.Control
                                                disabled
                                                value={value.valor}
                                            />
                                        </Col>
                                        <Col>
                                            <Form.Label>
                                                Base de cálculo
                                            </Form.Label>
                                            <Form.Control
                                                disabled
                                                value={value.basecalculo}
                                            />
                                        </Col>
                                        <Col>
                                            <div className="mb-3 flex space-x-2">
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip>
                                                            EDITAR
                                                        </Tooltip>
                                                    }
                                                >
                                                    <div className="mb-3 flex space-x-2 justify-center">
                                                        {user
                                                            .authorizationsByFeatureName[
                                                            FeaturesEnum.ITBI
                                                        ]?.canUpdate && (
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    handleEnableEdit(
                                                                        value.id
                                                                    )
                                                                }
                                                            >
                                                                <BsPencilSquare
                                                                    color="teal"
                                                                    size={20}
                                                                />
                                                            </button>
                                                        )}
                                                    </div>
                                                </OverlayTrigger>
                                            </div>
                                        </Col>
                                        <Col>
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                    <Tooltip>APAGAR</Tooltip>
                                                }
                                            >
                                                <div className="mb-3 flex space-x-2 justify-center">
                                                    {user
                                                        .authorizationsByFeatureName[
                                                        FeaturesEnum.ITBI
                                                    ]?.canRemove && (
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                handleDelete(
                                                                    value.id
                                                                )
                                                            }
                                                        >
                                                            <BsTrash
                                                                color="red"
                                                                size={20}
                                                            />
                                                        </button>
                                                    )}
                                                </div>
                                            </OverlayTrigger>
                                        </Col>
                                    </Row>
                                </div>
                            ))
                        ) : (
                            <h4>
                                Não há registros de ITBI para o ano de{" "}
                                {lastYear}
                            </h4>
                        )}
                    </div>
                )}
        </>
    );
};
